import { useState } from 'react';
import type { ChairReconciliation, SubscriptionItem } from '@salonomy/types';
import { Avatar, Button, Spinner } from '@salonomy/ui';
import { useReconcileChairs } from '../../data/billing.js';
import { initials, money, pluralize } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';

/**
 * The per-chair line WITH the staff list behind the count (Overview §6.7; brief 10 §3). The billable
 * count is DERIVED from active staff by the server — this component shows the subscription's per_chair
 * item (quantity × unit price, both server figures) and, on expand, the named staff the count was
 * computed from (fetched via reconcile, which returns the count + rate + the people behind it).
 *
 * "Recompute now" runs the server reconciliation: mid-cycle additions prorate, a removal never bills
 * more (the published rule). We DISPLAY the server's `applied`/count/staff result — no chair math here.
 */
export function ChairLine({ perChairItem }: { perChairItem: SubscriptionItem | undefined }) {
  const toast = useToast();
  const reconcile = useReconcileChairs();
  const [expanded, setExpanded] = useState(false);
  const [result, setResult] = useState<ChairReconciliation | null>(null);

  // The subscription's current per-chair item (the billed quantity × rate). When it's absent the plan
  // is a flat-only plan (no per-chair component) and there is nothing to show.
  if (!perChairItem) return null;

  const chairs = perChairItem.quantity;
  const ratePence = perChairItem.unitPricePence;

  async function loadStaff() {
    // Expand reveals the people behind the count. Reconcile is the only read that carries the staff
    // list; we surface its result but flag clearly when it actually changed the billed quantity.
    if (!expanded && !result) {
      try {
        const r = await reconcile.mutateAsync();
        setResult(r);
        if (r.applied) {
          toast.success('Chair count re-synced from your active team.');
        }
      } catch {
        toast.error('Couldn’t re-sync the chair count. Try again in a moment.');
        return;
      }
    }
    setExpanded((v) => !v);
  }

  const staff = result?.staff ?? [];

  return (
    <div style={{ paddingTop: 12, marginTop: 4, borderTop: '1px solid var(--slm-border)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>Per-chair</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            Billed per active team member. Adding a chair mid-cycle is pro-rated; removing one never
            bills you more.
          </div>
        </div>
        {/* The two SERVER figures — the billed quantity and the published rate — shown as the formula,
            NOT a client-multiplied total: mid-cycle proration means quantity × rate is not what the
            cycle actually bills. The exact charged amount is the invoice breakdown's per_chair line. */}
        <span className="billing-line__amount">
          {pluralize(chairs, 'chair')} × {money(ratePence)}
        </span>
      </div>

      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
        <Button size="sm" variant="ghost" onClick={loadStaff} disabled={reconcile.isPending}>
          {reconcile.isPending ? (
            <Spinner size={14} />
          ) : expanded ? (
            'Hide the team behind this'
          ) : (
            'Show the team behind this'
          )}
        </Button>
        {result && !result.applied ? (
          <span className="muted" style={{ fontSize: 11.5 }}>
            Already in sync with your team.
          </span>
        ) : null}
      </div>

      {expanded && staff.length > 0 ? (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {staff.map((s) => (
            <div key={s.staffId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Avatar initials={initials(s.displayName)} tone="slate" size={28} />
              <span style={{ fontSize: 13 }}>{s.displayName}</span>
            </div>
          ))}
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {pluralize(staff.length, 'billable chair')} from your active team.
          </div>
        </div>
      ) : expanded ? (
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          No billable chairs from your active team yet.
        </div>
      ) : null}
    </div>
  );
}
